import { type KeyboardEvent, type MouseEvent, useEffect, useId, useRef, useState } from 'react';

import { getNextTabbable, getTabbables } from '../../utils/getTabbables';

const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 100;

const isTabKey = (e: KeyboardEvent) =>
  e.key === 'Tab' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.nativeEvent.isComposing;

export type BadgePopoverCardName = {
  /** Names the card when it has no visible title. */
  label?: string;
  /** Id of the element that names the card, such as its title. */
  labelledBy?: string;
  /** Id of the element that describes the card. */
  describedBy?: string;
};

/**
 * Open state for a badge card: hover opens it without moving focus, while click, Enter and Space
 * open it with focus on the card so its name and description are read.
 */
export function useBadgePopover({ label, labelledBy, describedBy }: BadgePopoverCardName) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set when a click or key opened the card, so opening moves focus into it.
  const focusOnOpenRef = useRef(false);
  // Set while the open card came from hover, so a click moves into it instead of closing it.
  const openedByHoverRef = useRef(false);
  // Set by Escape, or a click that closes the card, so closing hands focus back to the trigger.
  const restoreFocusRef = useRef(false);
  const contentId = useId();

  const clearTimer = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, []);

  const focusIsInContent = () => contentRef.current?.contains(document.activeElement) ?? false;

  const scheduleClose = () => {
    clearTimer();
    // A card the user has moved focus into stays open until Escape, Tab or a click outside.
    if (focusIsInContent()) return;
    timerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  };

  const close = () => {
    clearTimer();
    setIsOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    clearTimer();
    setIsOpen(open);
  };

  const triggerProps = {
    ref: triggerRef,
    'aria-controls': isOpen ? contentId : undefined,
    onClick: (e: MouseEvent) => {
      // Keeps a row click or other parent handler from also firing.
      e.stopPropagation();
      clearTimer();
      if (isOpen && openedByHoverRef.current) {
        // Skips Radix's toggle so the click moves into the card instead of closing it.
        e.preventDefault();
        openedByHoverRef.current = false;
        contentRef.current?.focus({ preventScroll: true });
        return;
      }
      if (isOpen) {
        // Safari doesn't focus a clicked button, so focus would otherwise drop to the page when the card closes.
        restoreFocusRef.current = true;
        return;
      }
      focusOnOpenRef.current = true;
      openedByHoverRef.current = false;
    },
    onMouseEnter: () => {
      clearTimer();
      if (isOpen) return;
      timerRef.current = setTimeout(() => {
        focusOnOpenRef.current = false;
        openedByHoverRef.current = true;
        setIsOpen(true);
      }, OPEN_DELAY_MS);
    },
    onMouseLeave: () => {
      if (isOpen) scheduleClose();
      else clearTimer();
    },
  };

  // Radix loops Tab inside the portalled card, and blocks it in a card with no links, so both edges hand focus back out.
  const handleContentKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const content = contentRef.current;
    const trigger = triggerRef.current;
    if (!isTabKey(e) || !content || !trigger) return;
    const tabbables = getTabbables(content);
    const active = document.activeElement;
    if (e.shiftKey && (active === content || active === tabbables[0])) {
      e.preventDefault();
      trigger.focus();
      close();
    } else if (!e.shiftKey && (tabbables.length === 0 || active === tabbables[tabbables.length - 1])) {
      e.preventDefault();
      (getNextTabbable(trigger, content) ?? trigger).focus();
      close();
    }
  };

  const contentProps = {
    ref: contentRef,
    id: contentId,
    tabIndex: -1,
    'aria-label': label,
    'aria-labelledby': labelledBy,
    'aria-describedby': describedBy,
    onOpenAutoFocus: (e: Event) => {
      e.preventDefault();
      if (focusOnOpenRef.current) contentRef.current?.focus({ preventScroll: true });
      focusOnOpenRef.current = false;
    },
    onEscapeKeyDown: () => {
      restoreFocusRef.current = focusIsInContent() || document.activeElement === triggerRef.current;
    },
    onCloseAutoFocus: (e: Event) => {
      // Radix's own restore also runs after a hover close and skips a close from a click on the trigger.
      e.preventDefault();
      if (restoreFocusRef.current) triggerRef.current?.focus({ preventScroll: true });
      restoreFocusRef.current = false;
    },
    onClick: (e: MouseEvent) => {
      e.stopPropagation();
      // Activating a link or button closes the card, which would otherwise stay over a drawer that doesn't take focus.
      if ((e.target as Element).closest('a, button')) {
        restoreFocusRef.current = true;
        close();
      }
    },
    onKeyDown: handleContentKeyDown,
    onMouseEnter: clearTimer,
    onMouseLeave: scheduleClose,
  };

  return { isOpen, onOpenChange, triggerProps, contentProps };
}
